import React from "react";
import { Row, Col, Card, Tag } from "antd";
import TeamOutlined from "@ant-design/icons/es/icons/TeamOutlined";
import FlagOutlined from "@ant-design/icons/es/icons/FlagOutlined";
import EuroOutlined from "@ant-design/icons/es/icons/EuroOutlined";
import EnvironmentOutlined from "@ant-design/icons/es/icons/EnvironmentOutlined";
import ShopOutlined from "@ant-design/icons/es/icons/ShopOutlined";
import ScheduleOutlined from "@ant-design/icons/es/icons/ScheduleOutlined";
import TableOutlined from "@ant-design/icons/es/icons/TableOutlined";
import ClockCircleOutlined from "@ant-design/icons/es/icons/ClockCircleOutlined";
import SnippetsOutlined from "@ant-design/icons/es/icons/SnippetsOutlined";

import Localize from "../../components/Localized/Localize.comp";
import _Localize, { l } from "../../libs/localization";
import { label, translateRegistryOnView } from "../../libs/utils";

import CardField from "../../components/Cards/CardField";
import Label from "../../components/Label/Label";
import CardWiki from "../../components/Cards/CardWiki";
import CardComment from "../../components/Cards/CardComment";
import EntityNews from "../../components/EntityNews/EntityNews.component";
import PersonalComments from "../../components/Cards/PersonalComments";
import { OtherSocial } from "../../components/Cards/Social";
import CardContacts from "../../components/Cards/Card.contacts";
import CardAddress from "../../components/Cards/Card.address";
import useEntityNews from "../../hooks/useEntityNews";

/**
 * CompanyGeneral component
 * Used to display the general information of a company.
 * Responsive design handles both full-page detail view and constrained sidebar/dashboard views.
 */
const CompanyGeneral = ({
  data,
  entity,
  schema,
  features,
  permissions,
  match,
  sectorModal,
  info,
  columnLayout = "responsive", // "responsive" (side-by-side on lg) or "stacked" (always full width)
  visibleSections = null, // Array of strings: ['overview', 'wiki', 'publicComment', 'collectiveAgreements', 'entityNews', 'personalComments', 'links', 'contacts', 'address']
  hideTitle = false,
}) => {
  const isStacked = columnLayout === "stacked";
  const show = (section) =>
    !visibleSections || visibleSections.includes(section);

  const { data: entityNews } = useEntityNews(
    "company",
    data?._id,
    show("entityNews"),
  );

  return (
    <div className="grey-light-color">
      <Row gutter={[16, 16]} className="animation fadein-up slow" wrap={true}>
        {/* Main content column */}
        <Col
          span={24}
          lg={isStacked ? 24 : 16}
          style={{ minWidth: 0, flex: isStacked ? "1 1 100%" : "1 1 400px" }}
        >
          {show("overview") && (
            <Card
              title={
                <h2>
                  <b>
                    {Localize(
                      "PAGES.COMPANIES.DETAIL.SECTIONS.GENERAL.OVERVIEW",
                    )}
                  </b>
                </h2>
              }
              bordered={false}
              className="companyPage__body-card companyPage__body-card--custom"
            >
              <Row gutter={16}>
                <CardField
                  entity={entity}
                  data={data}
                  schema={schema}
                  val="creation_year"
                  icon={
                    <ClockCircleOutlined
                      style={{ fontSize: 20 }}
                      className="mr-2"
                    />
                  }
                />
                <CardField
                  entity={entity}
                  data={data}
                  schema={schema}
                  val="nation_orig"
                  icon={
                    <FlagOutlined style={{ fontSize: 20 }} className="mr-2" />
                  }
                />
                <CardField
                  entity={entity}
                  data={data}
                  schema={schema}
                  val="store_number"
                  icon={
                    <ShopOutlined style={{ fontSize: 20 }} className="mr-2" />
                  }
                />
                <CardField
                  entity={entity}
                  data={data}
                  schema={schema}
                  val="medium_planning"
                  icon={
                    <ScheduleOutlined
                      style={{ fontSize: 20 }}
                      className="mr-2"
                    />
                  }
                />
                <CardField
                  entity={entity}
                  data={data}
                  schema={schema}
                  val="income_range"
                  icon={
                    <EuroOutlined style={{ fontSize: 20 }} className="mr-2" />
                  }
                />
                <CardField
                  entity={entity}
                  data={data}
                  schema={schema}
                  val="employer_range"
                  icon={
                    <TeamOutlined style={{ fontSize: 20 }} className="mr-2" />
                  }
                />
                <CardField
                  entity={entity}
                  data={data}
                  schema={schema}
                  val="ateco_code"
                  icon={
                    <SnippetsOutlined
                      style={{ fontSize: 20 }}
                      className="mr-2"
                    />
                  }
                />
                {data.sector && data.sector.length > 0 && (
                  <Col span={24} className="mb-3">
                    <Row>
                      <TableOutlined
                        style={{ fontSize: 20 }}
                        className="mr-2"
                      />
                      <Label value={label(schema, "sector", entity)} />
                    </Row>
                    {data.sector.slice(0, 5).map((s) => (
                      <Tag className="m-1" key={s._id}>
                        {translateRegistryOnView(s._id, "Sector") || s.key}
                      </Tag>
                    ))}
                    {data.sector.length > 5 && (
                      <Tag
                        className="pointer"
                        onClick={() =>
                          sectorModal(
                            <Label value={_Localize("COMMON.SECTORS")} />,
                          )
                        }
                      >
                        + {data.sector.length - 5} {Localize("COMMON.SECTORS")}
                      </Tag>
                    )}
                  </Col>
                )}
                {data.international_invest &&
                  data.international_invest.length > 0 && (
                    <Col span={24} className="mb-3">
                      <Row>
                        <EnvironmentOutlined
                          style={{ fontSize: 20 }}
                          className="mr-2"
                        />
                        <Label
                          value={label(schema, "international_invest", entity)}
                        />
                      </Row>
                      {data.international_invest.slice(0, 5).map((s) => (
                        <Tag className="m-1" key={s}>
                          {translateRegistryOnView(
                            s,
                            "international_invest",
                            true,
                          )
                            ? translateRegistryOnView(
                                s,
                                "international_invest",
                                true,
                              )
                            : s}
                        </Tag>
                      ))}
                      {data.international_invest.length > 5 && (
                        <Tag
                          className="pointer"
                          onClick={() =>
                            info(
                              <Label
                                value={label(schema, "international_invest")}
                              />,
                              <ul
                                style={{
                                  columnCount: Math.ceil(
                                    data.international_invest.length / 15,
                                  ),
                                }}
                              >
                                {data.international_invest.map((s) => (
                                  <li className="m-1" key={s._id}>
                                    {translateRegistryOnView(
                                      s,
                                      "international_invest",
                                      true,
                                    )
                                      ? translateRegistryOnView(
                                          s,
                                          "international_invest",
                                          true,
                                        )
                                      : s}
                                  </li>
                                ))}
                              </ul>,
                            )
                          }
                        >
                          + {data.international_invest.length - 5}{" "}
                          {Localize("COMMON.COUNTRIES")}
                        </Tag>
                      )}
                    </Col>
                  )}
              </Row>
            </Card>
          )}
          {show("wiki") && <CardWiki data={data} />}
          {show("publicComment") && data.public_comment && (
            <CardComment comment={data.public_comment} />
          )}
          {show("collectiveAgreements") &&
            features.collectiveAgreements &&
            permissions.collectiveAgreements &&
            data.collectiveAgreements?.length && (
              <Card
                title={
                  <h2>
                    <b>
                      {Localize(
                        "PAGES.COMPANIES.DETAIL.SECTIONS.COLLECTIVE_AGREEMENTS.TITLE",
                      )}
                    </b>
                  </h2>
                }
                bordered={false}
                className="companyPage__body-card companyPage__body-card--custom mt-3"
              >
                {data.collectiveAgreements
                  ? data.collectiveAgreements.map((ca) => (
                      <p key={ca._id}>
                        <span
                          style={{
                            fontWeight: "bold",
                            opacity: 0.6,
                          }}
                        >
                          {ca.identifier}
                        </span>{" "}
                        - {ca.key}
                      </p>
                    ))
                  : null}
              </Card>
            )}
          {show("entityNews") && features.entityNews && entityNews?.length ? (
            <Card
              title={
                <h2>
                  <b>
                    {l("PAGES.COMPANIES.DETAIL.SECTIONS.ENTITY_NEWS.TITLE")}
                  </b>
                </h2>
              }
              bordered={false}
              className="companyPage__body-card companyPage__body-card--custom"
            >
              {entityNews.map((news) => (
                <EntityNews key={news._id} news={news} />
              ))}
            </Card>
          ) : null}
          {show("personalComments") &&
            features.comments &&
            permissions.comments && (
              <PersonalComments
                entityType="company"
                entityId={match.params.id}
                className="companyPage__body-card companyPage__body-card--custom"
                style={{
                  borderRadius: "10px",
                  overflow: "hidden",
                }}
              />
            )}
          {show("links") && (
            <Card
              title={
                <h2>
                  <b>
                    {Localize("PAGES.COMPANIES.DETAIL.SECTIONS.GENERAL.LINKS")}
                  </b>
                </h2>
              }
              bordered={false}
              className="companyPage__body-card companyPage__body-card--custom"
            >
              <OtherSocial external={data.external_links} />
            </Card>
          )}
        </Col>

        {/* Sidebar column */}
        {(show("contacts") || show("address")) && (
          <Col
            span={24}
            lg={isStacked ? 24 : 8}
            style={{ minWidth: 0, flex: isStacked ? "1 1 100%" : "1 1 300px" }}
          >
            {show("contacts") && <CardContacts data={data} />}
            {show("address") && (data.address || (data.addresses && data.addresses.length > 0)) && <CardAddress data={data} />}
          </Col>
        )}
      </Row>
    </div>
  );
};

export default CompanyGeneral;
